import { env } from './env';

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const parsePort = (value: string | undefined, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeBasePath = (value: string | undefined, fallback: string) => {
    const next = (value ?? fallback).trim() || fallback;
    if (/^https?:\/\//i.test(next)) {
        return trimTrailingSlash(next);
    }

    const withLeadingSlash = next.startsWith('/') ? next : `/${next}`;
    return trimTrailingSlash(withLeadingSlash);
};

const buildOrigin = (explicitUrl: string | undefined, fallbackProtocol: string, fallbackHost: string, fallbackPort: number) => {
    const normalizedExplicitUrl = explicitUrl?.trim();
    if (normalizedExplicitUrl) {
        return trimTrailingSlash(normalizedExplicitUrl);
    }

    return `${fallbackProtocol}://${fallbackHost}:${fallbackPort}`;
};

const DEFAULT_FRONTEND_PORT = 5173;
const DEFAULT_BACKEND_PORT = 8000;
const DEFAULT_FRONTEND_HOST = '127.0.0.1';
const DEFAULT_BACKEND_HOST = '127.0.0.1';

const frontendPort = parsePort(env.VITE_FRONTEND_PORT, DEFAULT_FRONTEND_PORT);
const backendPort = parsePort(env.VITE_BACKEND_PORT, DEFAULT_BACKEND_PORT);
const frontendProtocol = env.VITE_FRONTEND_PROTOCOL || 'http';
const backendProtocol = env.VITE_BACKEND_PROTOCOL || 'http';
const frontendHost = env.VITE_FRONTEND_HOST || DEFAULT_FRONTEND_HOST;
const backendHost = env.VITE_BACKEND_HOST || DEFAULT_BACKEND_HOST;

const frontendUrl = buildOrigin(env.VITE_FRONTEND_URL, frontendProtocol, frontendHost, frontendPort);
const backendUrl = buildOrigin(env.VITE_BACKEND_URL, backendProtocol, backendHost, backendPort);
const apiBaseUrl = normalizeBasePath(env.VITE_API_BASE_URL, '/api');
const mediaBaseUrl = normalizeBasePath(env.VITE_MEDIA_BASE_URL, '/media');

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

const buildAbsoluteUrl = (baseOrigin: string, basePath: string) => {
    if (isAbsoluteUrl(basePath)) {
        return trimTrailingSlash(basePath);
    }

    return `${trimTrailingSlash(baseOrigin)}${basePath}`;
};

export const appConfig = {
    appName: env.VITE_APP_NAME || 'Future World',
    appDescription: env.VITE_APP_DESCRIPTION || 'FW Corporate Website',
    frontend: {
        url: frontendUrl,
        host: frontendHost,
        port: frontendPort,
        protocol: frontendProtocol,
    },
    backend: {
        url: backendUrl,
        host: backendHost,
        port: backendPort,
        protocol: backendProtocol,
    },
    api: {
        baseUrl: apiBaseUrl,
        absoluteBaseUrl: buildAbsoluteUrl(backendUrl, apiBaseUrl),
    },
    media: {
        baseUrl: mediaBaseUrl,
        absoluteBaseUrl: buildAbsoluteUrl(backendUrl, mediaBaseUrl),
    },
    defaultLocale: env.VITE_DEFAULT_LOCALE || 'zh',
};

export const getApiBaseCandidates = () => {
    const bases = new Set<string>();
    bases.add(appConfig.api.baseUrl);
    bases.add(appConfig.api.absoluteBaseUrl);

    if (!isAbsoluteUrl(appConfig.api.baseUrl) && typeof window !== 'undefined') {
        const hostname = window.location.hostname || appConfig.backend.host || DEFAULT_BACKEND_HOST;
        const protocol = window.location.protocol === 'https:' ? 'https:' : `${appConfig.backend.protocol}:`;
        bases.add(`${protocol}//${hostname}:${appConfig.backend.port}${appConfig.api.baseUrl}`);
        bases.add(`http://127.0.0.1:${appConfig.backend.port}${appConfig.api.baseUrl}`);
        bases.add(`http://localhost:${appConfig.backend.port}${appConfig.api.baseUrl}`);
    }

    return Array.from(bases).map((base) => trimTrailingSlash(base));
};

const isLoopbackHost = (hostname: string) => hostname === '127.0.0.1' || hostname === 'localhost';

const getCurrentOrigin = () => {
    if (typeof window === 'undefined') return null;
    return trimTrailingSlash(window.location.origin);
};

export const resolveBackendAssetUrl = (value?: string | null) => {
    if (!value) return null;

    if (!isAbsoluteUrl(value)) {
        const normalizedPath = value.startsWith('/') ? value : `/${value}`;
        const currentOrigin = getCurrentOrigin();
        if (currentOrigin) {
            return `${currentOrigin}${normalizedPath}`;
        }

        return `${trimTrailingSlash(appConfig.backend.url)}${normalizedPath}`;
    }

    try {
        const original = new URL(value);
        if (typeof window === 'undefined') {
            return original.toString();
        }

        const currentHost = window.location.hostname || '';
        if (isLoopbackHost(original.hostname) && currentHost && !isLoopbackHost(currentHost)) {
            return `${getCurrentOrigin() ?? trimTrailingSlash(appConfig.backend.url)}${original.pathname}${original.search}${original.hash}`;
        }

        return original.toString();
    } catch {
        return value;
    }
};

export const isAbsoluteHttpUrl = isAbsoluteUrl;
